import type { ValueResponseList } from '../types/base';
import { computedAsync } from '@vueuse/core';
import { useState } from 'nuxt/app';
import { defineStore } from 'pinia';
import { readonly, ref } from 'vue';
import { useApi } from '../utils/useApi';

export const useCountryStore = defineStore('countryStore', () => {
	const $api = useApi();
	const loading = ref(false);
	const error = ref<unknown>(null);
	// Use useState for SSR isolation - ref() shares state across requests on the server
	const countriesCache = useState<CountryResponse[]>('store:countriesCache', () => []);
	// Tracks a completed fetch independently of the cache being non-empty, so a
	// legitimately empty result doesn't re-trigger fetchCountries() forever.
	const fetched = useState<boolean>('store:countriesFetched', () => false);
	// Use useState to avoid module-level variable leaking between SSR requests
	const fetchPromise = useState<Promise<CountryResponse[]> | null>('store:countriesFetchPromise', () => null);

	const defaultCountryCode = useState<string>('store:defaultCountryCode', () => 'DE');
	// Dedup state for initDefaultCountry: `initialized` blocks repeat calls
	// after success; `initPromise` joins concurrent in-flight calls.
	const initialized = useState<boolean>('store:defaultCountryInitialized', () => false);
	const initPromise = useState<Promise<void> | null>('store:defaultCountryInitPromise', () => null);

	async function fetchCountries() {
		if (fetched.value) {
			return countriesCache.value;
		}
		if (fetchPromise.value) {
			return fetchPromise.value;
		}
		error.value = null;
		fetchPromise.value = $api<ValueResponseList<CountryResponse>>('/api/countries')
			.then((res) => {
				countriesCache.value = res.values ?? [];
				fetched.value = true;
				return countriesCache.value;
			})
			.catch((e) => {
				error.value = e;
				throw e;
			})
			.finally(() => {
				fetchPromise.value = null;
			});
		return fetchPromise.value;
	}

	const asyncCountries = computedAsync<CountryResponse[]>(() => {
		if (fetched.value) {
			return countriesCache.value;
		}

		// Don't auto-retry after a failed fetch, otherwise resetting fetchPromise
		// in finally() re-triggers this computed and loops forever.
		if (error.value) {
			return [];
		}

		return fetchCountries();
	}, [], {
		lazy: true,
		evaluating: loading,
	});

	async function getCountryFromIp(): Promise<CountryResponse | null> {
		const data = await $api<{
			countryCode: string;
		}>('/internal/country');

		if (!data?.countryCode) {
			return null;
		}

		const countries = await fetchCountries();
		const normalizedCountryCode = data.countryCode.toUpperCase();

		return countries.find(c => c.twoLetterCountryCode === normalizedCountryCode) ?? null;
	}

	async function initDefaultCountry(): Promise<void> {
		if (initialized.value) {
			return;
		}
		if (initPromise.value) {
			return initPromise.value;
		}

		// Only hits /internal/country (provided by hm-common, available in every
		// app). Intentionally does NOT call fetchCountries() — that endpoint
		// only exists in apps with a country-aware backend, but every app
		// needs to know its default country code for captcha geo-gating.
		initPromise.value = (async () => {
			try {
				const data = await $api<{ countryCode: string }>('/internal/country');
				if (data?.countryCode) {
					defaultCountryCode.value = data.countryCode.toUpperCase();
				}
				initialized.value = true;
			}
			finally {
				initPromise.value = null;
			}
		})();

		return initPromise.value;
	}

	return {
		asyncCountries,
		listCountries: fetchCountries,
		loading,
		getCountryFromIp,
		initDefaultCountry,
		defaultCountryCode: readonly(defaultCountryCode),
	};
});

interface StateResponse {
	displayName: string;
	stateCode: string;
}

export type CountryStateValidation
	/**
	 *  Addresses must not have a state.
	 */
	= 'NoState'
	/**
	 *  Addresses can have a state, it will be remembered verbatim and not validated.
	 */
		| 'Optional'
	/**
	 *  Addresses can have a state, it has to be validated against a set of possibilities, and its code is stored.
	 */
		| 'OptionalAndValidated'
	/**
	 *  Addresses must have a state, it has to be validated against a set of possibilities, and its code is stored.
	 */
		| 'RequiredAndValidated';

export interface CountryResponse {
	id: number;
	twoLetterCountryCode: string;
	engName: string;
	nativeNames: string[];
	idds: number[];

	states?: StateResponse[];
	stateValidation?: CountryStateValidation;
	stateUnitName?: string;
}
